/**
 * Bling AI — Conversational Commerce Renderer
 *
 * Stateful product cards. Cards mutate in place across re-renders.
 * Each card is keyed by `${messageIdx}-${productId}` so the SAME card persists
 * through streaming updates, customisation flows, and preview generation.
 *
 * Card state machine:
 *   idle → customizing → previewing → previewed → saved
 *
 * Block types parsed from AI output:
 *   [[product:NUMERIC_ID]]                  → product card
 *   ```bling-preview\n{json}\n```           → mutates a card image when generated
 */
window.BlingAIRender = (function () {
  // ──────────────────────────────────────────────────────────────────
  //  State store (per-document)
  // ──────────────────────────────────────────────────────────────────
  const cardState = new Map(); // key → { product, customization, previewUrl, previewRequestId, status }

  function getCardState(key) {
    return cardState.get(key) || null;
  }
  function setCardState(key, patch) {
    const cur = cardState.get(key) || {};
    cardState.set(key, { ...cur, ...patch });
    return cardState.get(key);
  }

  // ──────────────────────────────────────────────────────────────────
  //  Utilities
  // ──────────────────────────────────────────────────────────────────
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ──────────────────────────────────────────────────────────────────
  //  Markdown → HTML
  // ──────────────────────────────────────────────────────────────────
  function markdownToHtml(text) {
    if (!text) return '';
    let html = escapeHtml(text);

    // Inline code
    html = html.replace(/`([^`\n]+)`/g, '<code class="bai-code">$1</code>');

    // Headings
    html = html.replace(/^### (.+)$/gm, '<h4 class="bai-h4">$1</h4>');
    html = html.replace(/^## (.+)$/gm, '<h3 class="bai-h3">$1</h3>');
    html = html.replace(/^# (.+)$/gm, '<h2 class="bai-h2">$1</h2>');

    // Bold / italic
    html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/(^|[\s(])\*([^*\n]+)\*/g, '$1<em>$2</em>');

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, url) => {
      const safe = url.replace(/"/g, '%22');
      return `<a class="bai-link" href="${safe}" target="_blank" rel="noopener">${label}</a>`;
    });

    // Unordered lists
    html = html.replace(/(?:^|\n)((?:[-*] .+\n?)+)/g, (block) => {
      const items = block
        .trim().split('\n')
        .map((l) => { const m = l.match(/^[-*] (.+)$/); return m ? `<li>${m[1]}</li>` : ''; })
        .filter(Boolean).join('');
      return items ? `\n<ul class="bai-ul">${items}</ul>\n` : block;
    });

    // Numbered lists
    html = html.replace(/(?:^|\n)((?:\d+\. .+\n?)+)/g, (block) => {
      const items = block
        .trim().split('\n')
        .map((l) => { const m = l.match(/^\d+\. (.+)$/); return m ? `<li>${m[1]}</li>` : ''; })
        .filter(Boolean).join('');
      return items ? `\n<ol class="bai-ol">${items}</ol>\n` : block;
    });

    // Paragraphs
    html = html.split(/\n{2,}/).map((c) => {
      const t = c.trim();
      if (!t) return '';
      if (/^<(h[1-6]|ul|ol|pre|p|table|div|blockquote|img)/i.test(t)) return c;
      return `<p class="bai-p">${t.replace(/\n/g, '<br>')}</p>`;
    }).join('\n');

    return html;
  }

  // ──────────────────────────────────────────────────────────────────
  //  Block extractor
  // ──────────────────────────────────────────────────────────────────
  function extractBlocks(text) {
    if (!text) return [];
    const blocks = [];
    let cursor = 0;
    // Match fenced blocks OR product tokens, in document order.
    const re = /(?:```([\w-]+)\n([\s\S]*?)```)|(?:\[\[product:(\d+)\]\])/g;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > cursor) {
        const md = text.slice(cursor, m.index);
        if (md.trim()) blocks.push({ type: 'markdown', content: md.trim() });
      }
      if (m[1] && m[2] !== undefined) {
        const lang = m[1];
        const inner = m[2].trim();
        if (lang === 'bling-preview') {
          let spec = {};
          try { spec = JSON.parse(inner); } catch { spec = { note: inner }; }
          blocks.push({ type: 'preview', spec });
        } else {
          blocks.push({ type: 'markdown', content: '```' + lang + '\n' + inner + '\n```' });
        }
      } else if (m[3]) {
        blocks.push({ type: 'product-token', productId: m[3] });
      }
      cursor = re.lastIndex;
    }
    if (cursor < text.length) {
      const tail = text.slice(cursor).trim();
      if (tail) blocks.push({ type: 'markdown', content: tail });
    }
    // Defensive: strip any leaked sentinels
    return blocks.map((b) =>
      b.type === 'markdown'
        ? { ...b, content: b.content.replace(/%%FENCE_[\w-]+%%[\s\S]*?%%ENDFENCE%%/g, '').replace(/%%PRODUCT_CARDS%%|%%PREVIEW_BLOCK%%/g, '') }
        : b,
    ).filter((b) => b.type !== 'markdown' || b.content.trim().length > 0);
  }

  // ──────────────────────────────────────────────────────────────────
  //  Product fetching
  // ──────────────────────────────────────────────────────────────────
  const _inflight = new Map();
  async function fetchProduct(id, apiBase, cache) {
    if (cache[id]) return cache[id];
    if (_inflight.has(id)) return _inflight.get(id);
    const p = (async () => {
      try {
        const res = await fetch(`${apiBase}/api/ai/products/${encodeURIComponent(id)}`);
        const json = await res.json();
        if (json.data?.product) {
          const p = json.data.product;
          cache[id] = {
            id: p.productId,
            title: p.title,
            handle: p.handle,
            imageUrl: p.imageUrl,
            price: p.price,
            currency: p.currency,
            variants: p.variants || [],
            variantId: p.variants?.[0]?.variantId || '',
            customization: p.customization || { enabled: false, fields: [] },
            preview: p.preview || { hasTemplate: false, zones: [] },
            productUrl: p.productUrl,
          };
          return cache[id];
        }
      } catch (e) {
        console.warn('[BlingAI] product fetch', id, e);
      }
      return null;
    })();
    _inflight.set(id, p);
    try { return await p; } finally { _inflight.delete(id); }
  }

  // ──────────────────────────────────────────────────────────────────
  //  Render a single product card (stateful)
  // ──────────────────────────────────────────────────────────────────
  function renderProductCard(card, key, opts) {
    if (!card) return '';
    const { compact = false } = opts || {};
    const state = getCardState(key) || {};
    const previewUrl = state.previewUrl || null;
    const status = state.status || 'idle';
    const customization = state.customization || {};
    const id = card.id;
    const variantId = card.variantId || '';
    const url = card.productUrl || (card.handle ? `/products/${card.handle}` : '#');
    const img = previewUrl || card.imageUrl || '';
    const title = card.title || 'Product';
    const price = card.price ? (card.currency ? `${card.currency} ${card.price}` : card.price) : '';
    const compactClass = compact ? ' bai-card--compact' : '';
    // Every product is customisable via free-form AI instructions
    const customizable = false;

    // Free-form customisation drawer (single conversational input)
    let drawerHtml = '';
    if (status === 'customizing' || status === 'previewing' || status === 'previewed' || status === 'saved') {
      const freeformVal = escapeHtml(customization.freeform || '');
      const genLabel = status === 'previewing'
        ? 'Generating…'
        : (status === 'previewed' || status === 'saved') ? 'Regenerate' : 'Generate preview';

      drawerHtml = `
        <div class="bai-card-drawer">
          <label class="bai-field">
            <span class="bai-field-label">What customisation would you like?</span>
            <textarea class="bai-cust-textarea" rows="2" maxlength="400"
              placeholder="e.g. luxury black and gold, my name Asad in Arabic calligraphy, floral wedding theme…"
              data-cust-key="freeform">${freeformVal}</textarea>
          </label>
          <div class="bai-card-drawer-actions">
            <button type="button" class="bai-btn bai-btn--primary" data-action="generate-preview" data-card-key="${escapeHtml(key)}" ${status === 'previewing' ? 'disabled' : ''}>
              ${genLabel}
            </button>
            ${status === 'previewed' || status === 'saved' ? `
              <button type="button" class="bai-btn bai-btn--cart" data-action="save-customization" data-card-key="${escapeHtml(key)}" ${status === 'saved' ? 'disabled' : ''}>
                ${status === 'saved' ? 'Saved ✓' : 'Save design'}
              </button>` : ''}
          </div>
        </div>`;
    }

    const isCustomActive = status !== 'idle';
    const customisedBadge = previewUrl ? `<span class="bai-badge">✦ Customised</span>` : '';

    return `
      <article class="bai-card${compactClass}" data-card-key="${escapeHtml(key)}" data-product-id="${escapeHtml(id)}" data-variant-id="${escapeHtml(variantId)}" data-card-status="${escapeHtml(status)}">
        <div class="bai-card-media">
          ${img ? `<img class="bai-card-img" src="${escapeHtml(img)}" alt="${escapeHtml(title)}" loading="lazy" />` : '<div class="bai-card-img bai-card-img--empty">No image</div>'}
          ${customisedBadge}
        </div>
        <div class="bai-card-body">
          <p class="bai-card-title">${escapeHtml(title)}</p>
          ${price ? `<p class="bai-card-price">${escapeHtml(price)}</p>` : ''}
          <div class="bai-card-actions">
            ${variantId ? `<button type="button" class="bai-btn bai-btn--cart" data-action="add-to-cart" data-variant-id="${escapeHtml(variantId)}" data-title="${escapeHtml(title)}" data-card-key="${escapeHtml(key)}">${previewUrl ? 'Add design to cart' : 'Add to cart'}</button>` : ''}
            ${variantId ? `<button type="button" class="bai-btn bai-btn--buynow" data-action="buy-now" data-variant-id="${escapeHtml(variantId)}" data-card-key="${escapeHtml(key)}">Buy now</button>` : ''}
            ${customizable ? `<button type="button" class="bai-btn ${isCustomActive ? 'bai-btn--primary' : 'bai-btn--ghost'}" data-action="toggle-customize" data-card-key="${escapeHtml(key)}">${isCustomActive ? 'Close' : 'Customise'}</button>` : ''}
            <a class="bai-btn bai-btn--link" href="${escapeHtml(url)}" target="_blank" rel="noopener">View →</a>
          </div>
          ${drawerHtml}
        </div>
      </article>`;
  }

  // ──────────────────────────────────────────────────────────────────
  //  Render all blocks
  // ──────────────────────────────────────────────────────────────────
  async function renderBlocks(blocks, opts) {
    const { apiBase = '', shopHost = '', productCache = {}, compact = false, messageKey = 'm' } = opts || {};

    // Resolve product tokens
    const tokenBlocks = blocks.filter((b) => b.type === 'product-token');
    await Promise.all(tokenBlocks.map((b) => fetchProduct(b.productId, apiBase, productCache)));

    // Map of products that appeared (id → card key) so preview blocks can pair to them
    const productKeys = {};

    // First pass — assign keys to each product appearance
    let cardCounter = 0;
    for (const b of blocks) {
      if (b.type === 'product-token') {
        const key = `${messageKey}-p${cardCounter++}-${b.productId}`;
        b._cardKey = key;
        if (!productKeys[b.productId]) productKeys[b.productId] = key;
        // initialize state entry preserving previous state (if same key reappears across re-renders)
        if (!cardState.has(key)) {
          cardState.set(key, { product: productCache[b.productId], status: 'idle' });
        } else {
          // Keep prior state but refresh product data
          setCardState(key, { product: productCache[b.productId] });
        }
      }
    }

    // Second pass — render
    const parts = [];
    let cardGroup = [];
    const flushGroup = () => {
      if (cardGroup.length) {
        parts.push(`<div class="bai-cards">${cardGroup.join('')}</div>`);
        cardGroup = [];
      }
    };

    for (const b of blocks) {
      if (b.type === 'markdown') {
        flushGroup();
        const md = markdownToHtml(b.content);
        if (md.trim()) parts.push(md);
      } else if (b.type === 'product-token') {
        const card = productCache[b.productId];
        if (card) {
          cardGroup.push(renderProductCard(card, b._cardKey, { compact }));
        }
      } else if (b.type === 'preview') {
        // Pair preview with the latest product card emitted
        flushGroup();
        const targetProductId = String(b.spec.product_id || '');
        const targetKey = productKeys[targetProductId] || Object.values(productKeys).pop();
        if (targetKey) {
          // Auto-fill customization from spec.zone_values
          const cur = cardState.get(targetKey) || {};
          const merged = { ...cur.customization, ...(b.spec.zone_values || {}) };
          setCardState(targetKey, {
            customization: merged,
            status: cur.status === 'previewed' || cur.status === 'saved' ? cur.status : 'customizing',
            pendingPreviewNote: b.spec.note || null,
          });
          parts.push(`<p class="bai-preview-hint">✦ Tap <strong>Customise</strong> on the product card above${b.spec.note ? ` to render: <em>${escapeHtml(b.spec.note)}</em>` : ''}</p>`);
        } else {
          // No product context — show a generic preview card
          parts.push(`<div class="bai-preview-orphan">Select a product to generate this preview.</div>`);
        }
      }
    }
    flushGroup();

    return parts.join('\n');
  }

  // ──────────────────────────────────────────────────────────────────
  //  Cart actions (Shopify storefront AJAX API)
  // ──────────────────────────────────────────────────────────────────
  async function addToCart(variantId, quantity, properties) {
    try {
      const item = { id: parseInt(variantId, 10), quantity: quantity || 1 };
      if (properties && Object.keys(properties).length) item.properties = properties;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        console.warn('[BlingAI] cart/add failed', res.status, t);
        return false;
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return true;
    } catch (e) {
      console.warn('[BlingAI] cart/add error', e);
      return false;
    }
  }

  // ──────────────────────────────────────────────────────────────────
  //  Card action handler — bound to message container
  // ──────────────────────────────────────────────────────────────────
  async function handleCardAction(e, hooks) {
    const {
      apiBase, shopHost, sessionId, conversationId,
      onCartFeedback, onRerender,
      onPreviewLoading, onPreviewGenerated, onPreviewError,
      activeProduct,
    } = hooks || {};

    // INPUT / CHANGE events ─────────────────────────────────────────
    if (e.type === 'input' || e.type === 'change') {
      const input = e.target.closest('[data-cust-key]');
      if (!input) return false;
      const card = input.closest('.bai-card');
      if (!card) return false;
      const key = card.dataset.cardKey;
      const fieldId = input.dataset.custKey;
      const isFile = input.dataset.custFile === '1';
      if (isFile && input.files && input.files[0]) {
        const file = input.files[0];
        await uploadCustomImage(file, apiBase, key, fieldId);
        if (typeof onRerender === 'function') await onRerender();
      } else if (!isFile) {
        // Update state silently — do NOT rerender on every keystroke to keep focus + perf.
        const cur = cardState.get(key) || {};
        setCardState(key, { customization: { ...cur.customization, [fieldId]: input.value } });
      }
      return true;
    }

    // CLICK events only beyond this point ───────────────────────────
    if (e.type !== 'click') return false;

    // Toggle customize drawer
    const tBtn = e.target.closest('[data-action="toggle-customize"]');
    if (tBtn) {
      const key = tBtn.dataset.cardKey;
      const cur = cardState.get(key) || {};
      const nextStatus = cur.status === 'idle' || !cur.status ? 'customizing' : 'idle';
      setCardState(key, { status: nextStatus });
      if (typeof onRerender === 'function') await onRerender();
      return true;
    }

    // Generate preview
    const gBtn = e.target.closest('[data-action="generate-preview"]');
    if (gBtn) {
      const key = gBtn.dataset.cardKey;
      await generatePreview(key, {
        apiBase, sessionId, onRerender,
        onPreviewLoading, onPreviewGenerated, onPreviewError,
        activeProduct,
      });
      return true;
    }

    // Save customization
    const sBtn = e.target.closest('[data-action="save-customization"]');
    if (sBtn) {
      const key = sBtn.dataset.cardKey;
      await saveCustomization(key, { apiBase, sessionId, conversationId, onRerender, onCartFeedback });
      return true;
    }

    // Add to cart
    const cartBtn = e.target.closest('[data-action="add-to-cart"]');
    if (cartBtn) {
      const variantId = cartBtn.dataset.variantId;
      const title = cartBtn.dataset.title || 'Item';
      const key = cartBtn.dataset.cardKey;
      const state = cardState.get(key) || {};
      const props = state.previewUrl ? {
        '_Bling Customisation': 'Yes',
        'Design preview': state.previewUrl,
        ...Object.fromEntries(Object.entries(state.customization || {}).filter(([k]) => !k.startsWith('_'))),
      } : null;

      if (!variantId) return true;
      const orig = cartBtn.textContent;
      cartBtn.disabled = true;
      cartBtn.textContent = 'Adding…';
      const ok = await addToCart(variantId, 1, props);
      cartBtn.disabled = false;
      cartBtn.textContent = ok ? 'Added ✓' : 'Try again';
      if (ok && typeof onCartFeedback === 'function') onCartFeedback(title);
      setTimeout(() => { cartBtn.textContent = orig; }, 2800);
      return true;
    }

    // Buy now
    const buyBtn = e.target.closest('[data-action="buy-now"]');
    if (buyBtn) {
      const variantId = buyBtn.dataset.variantId;
      const key = buyBtn.dataset.cardKey;
      const state = cardState.get(key) || {};
      const props = state.previewUrl ? {
        '_Bling Customisation': 'Yes',
        'Design preview': state.previewUrl,
        ...Object.fromEntries(Object.entries(state.customization || {}).filter(([k]) => !k.startsWith('_'))),
      } : null;

      if (!variantId) return true;
      buyBtn.disabled = true;
      buyBtn.textContent = 'Processing…';
      const ok = await addToCart(variantId, 1, props);
      if (ok) {
        window.location.href = '/checkout';
      } else {
        buyBtn.disabled = false;
        buyBtn.textContent = 'Buy now';
      }
      return true;
    }

    return false;
  }

  // ──────────────────────────────────────────────────────────────────
  //  Upload image for a customization field
  // ──────────────────────────────────────────────────────────────────
  async function uploadCustomImage(file, apiBase, cardKey, fieldId) {
    try {
      if (file.size > 8 * 1024 * 1024) { toast('Image too large (max 8MB)'); return; }
      const presign = await fetch(`${apiBase}/api/uploads/presigned`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_name: file.name, mime_type: file.type, file_size: file.size }),
      });
      const pj = await presign.json();
      if (pj.error) throw new Error(pj.error);
      const put = await fetch(pj.data.upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!put.ok) throw new Error('Upload failed: ' + put.status);

      const cur = cardState.get(cardKey) || {};
      const fileUrl = `${apiBase}/api/uploads/file?key=${encodeURIComponent(pj.data.r2_key)}`;
      setCardState(cardKey, {
        customization: {
          ...cur.customization,
          [fieldId]: fileUrl,
          ['_upload_' + fieldId]: pj.data.r2_key, // keep r2 key for preview API
        },
      });
      toast('Image uploaded ✓');
    } catch (e) {
      console.warn('[BlingAI] upload', e);
      toast('Upload failed: ' + (e.message || 'unknown'));
    }
  }

  // ──────────────────────────────────────────────────────────────────
  //  Generate preview (calls /api/ai/preview/generate)
  // ──────────────────────────────────────────────────────────────────
  async function generatePreview(cardKey, opts) {
    const { apiBase, sessionId, onRerender, onPreviewLoading, onPreviewGenerated, onPreviewError, activeProduct } = opts || {};
    const state = cardState.get(cardKey);
    if (!state || !state.product) { toast('Select a product first.'); return; }

    const freeformPrompt = (state.customization?.freeform || '').trim();
    if (!freeformPrompt) {
      toast('Please describe the customisation you want.');
      return;
    }

    setCardState(cardKey, { status: 'previewing' });
    if (typeof onRerender === 'function') await onRerender();
    if (typeof onPreviewLoading === 'function') onPreviewLoading('Generating your customised preview…');

    try {
      const product = state.product;
      const res = await fetch(`${apiBase}/api/ai/preview/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shopify_product_id: product.id,
          session_id: sessionId,
          freeform_prompt: freeformPrompt,
          product_image_url: product.imageUrl || null,
          use_ai: true,
          zone_values: {},
        }),
      });
      const json = await res.json();

      // 429 = capacity limit — show professional message
      if (res.status === 429) {
        const infraMsg = 'We are experiencing high AI image traffic right now. Please try again in a moment.';
        setCardState(cardKey, { status: 'customizing' });
        if (typeof onPreviewError === 'function') onPreviewError(infraMsg);
        toast(infraMsg);
        if (typeof onRerender === 'function') await onRerender();
        return;
      }

      if (!res.ok || json.error) throw new Error(json.error || `${res.status}`);
      const previewId = json.data?.preview_id;
      const r2Key = json.data?.output_r2_key;
      if (!previewId || !r2Key) throw new Error('No preview returned from server');
      const previewUrl = `${apiBase}/api/uploads/file?key=${encodeURIComponent(r2Key)}`;

      setCardState(cardKey, {
        status: 'previewed',
        previewUrl,
        previewRequestId: previewId,
        previewR2Key: r2Key,
      });
      if (typeof onPreviewGenerated === 'function') onPreviewGenerated(previewUrl, product.title);
      toast('Preview ready ✓');
    } catch (e) {
      console.warn('[BlingAI] preview', e);
      setCardState(cardKey, { status: 'customizing' });
      const infraMsg = 'Image generation is temporarily unavailable. Please retry in a moment.';
      if (typeof onPreviewError === 'function') onPreviewError(infraMsg);
      toast(infraMsg);
    }
    if (typeof onRerender === 'function') await onRerender();
  }

  // ──────────────────────────────────────────────────────────────────
  //  Save customization
  // ──────────────────────────────────────────────────────────────────
  async function saveCustomization(cardKey, opts) {
    const { apiBase, sessionId, conversationId, onCartFeedback, onRerender } = opts;
    const state = cardState.get(cardKey);
    if (!state || !state.product) return;
    try {
      const cust = state.customization || {};
      const zoneValues = Object.fromEntries(Object.entries(cust).filter(([k]) => !k.startsWith('_')));
      const uploadKeys = Object.fromEntries(
        Object.entries(cust).filter(([k]) => k.startsWith('_upload_')).map(([k, v]) => [k.replace('_upload_', ''), v]),
      );

      const res = await fetch(`${apiBase}/api/ai/customizations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          conversation_id: conversationId,
          shopify_product_id: state.product.id,
          shopify_product_title: state.product.title,
          shopify_variant_id: state.product.variantId,
          zone_values: zoneValues,
          upload_r2_keys: uploadKeys,
          preview_request_id: state.previewRequestId,
          preview_r2_key: state.previewR2Key,
        }),
      });
      const j = await res.json();
      if (j.error) throw new Error(j.error);
      setCardState(cardKey, { status: 'saved', savedId: j.data?.customization_id });
      toast('Design saved ✓');
      if (typeof onCartFeedback === 'function') onCartFeedback('Design saved');
    } catch (e) {
      toast('Save failed: ' + (e.message || 'unknown'));
    }
    if (typeof onRerender === 'function') await onRerender();
  }

  // ──────────────────────────────────────────────────────────────────
  //  Toast
  // ──────────────────────────────────────────────────────────────────
  function toast(message) {
    injectSharedCss();
    let el = document.getElementById('bai-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'bai-toast';
      el.className = 'bai-toast';
      document.body.appendChild(el);
    }
    el.textContent = message;
    requestAnimationFrame(() => el.classList.add('show'));
    clearTimeout(toast._t);
    toast._t = setTimeout(() => el.classList.remove('show'), 2400);
  }

  // ──────────────────────────────────────────────────────────────────
  //  Shared CSS
  // ──────────────────────────────────────────────────────────────────
  let cssInjected = false;
  function injectSharedCss() {
    if (cssInjected) return;
    cssInjected = true;
    const style = document.createElement('style');
    style.id = 'bai-shared-css';
    style.textContent = `
      .bai-msg{display:flex;width:100%}
      .bai-msg--user{justify-content:flex-end}
      .bai-msg--assistant{justify-content:flex-start;flex-direction:column;align-items:stretch}
      .bai-msg--user .bai-msg-bubble{background:var(--bai-user-bg,#18181b);color:var(--bai-user-fg,#fff);border-radius:14px 14px 4px 14px;padding:10px 14px;max-width:min(640px,88%);font-size:14px;line-height:1.5;word-wrap:break-word}
      .bai-msg--assistant .bai-msg-bubble--rich{background:var(--bai-assistant-bg,#fff);color:var(--bai-assistant-fg,#18181b);border:1px solid var(--bai-border,#e4e4e7);border-radius:4px 14px 14px 14px;padding:14px 16px;max-width:min(760px,96%);font-size:14px;line-height:1.6;word-wrap:break-word;box-shadow:0 1px 3px rgba(0,0,0,.04)}
      .bai-msg-img{max-width:260px;max-height:260px;border-radius:10px;display:block;margin-top:6px;border:1px solid var(--bai-border,#e4e4e7)}
      .bai-h2{margin:14px 0 6px;font-size:1.15em;font-weight:700}
      .bai-h3{margin:12px 0 6px;font-size:1.05em;font-weight:700}
      .bai-h4{margin:10px 0 4px;font-size:1em;font-weight:600}
      .bai-p{margin:0 0 10px}
      .bai-p:last-child{margin-bottom:0}
      .bai-ul,.bai-ol{margin:0 0 10px;padding-left:1.4rem}
      .bai-link{color:var(--bai-accent,#2563eb);text-decoration:underline}
      .bai-code{background:var(--bai-code-bg,#f1f5f9);padding:1px 5px;border-radius:4px;font-size:.92em;font-family:ui-monospace,Menlo,monospace}

      .bai-cards{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px;margin:14px 0}
      .bai-card{border:1px solid var(--bai-border,#e4e4e7);border-radius:var(--bai-card-radius,14px);overflow:hidden;background:var(--bai-card-bg,#fff);transition:box-shadow .15s,transform .15s;position:relative;display:flex;flex-direction:column}
      .bai-card:hover{box-shadow:0 6px 24px rgba(0,0,0,.08);transform:translateY(-1px)}
      .bai-card--compact{grid-template-columns:88px 1fr;display:grid;align-items:stretch}
      .bai-card-media{position:relative;background:#f4f4f5}
      .bai-card-img{width:100%;height:180px;object-fit:cover;display:block;background:#f4f4f5;transition:opacity .25s}
      .bai-card--compact .bai-card-img{width:88px;height:88px}
      .bai-card-img--empty{display:flex;align-items:center;justify-content:center;color:#a1a1aa;font-size:11px}
      .bai-badge{position:absolute;top:8px;left:8px;background:rgba(255,255,255,.95);color:#18181b;font-size:10px;font-weight:700;padding:3px 8px;border-radius:999px;text-transform:uppercase;letter-spacing:.04em;box-shadow:0 1px 3px rgba(0,0,0,.1)}
      .bai-card-body{padding:11px 13px;display:flex;flex-direction:column;gap:6px;flex:1}
      .bai-card--compact .bai-card-body{padding:8px 12px}
      .bai-card-title{margin:0;font-size:13px;font-weight:600;color:var(--bai-card-fg,#18181b);line-height:1.35;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
      .bai-card-price{margin:0;font-size:13px;color:var(--bai-card-meta,#52525b);font-weight:700}
      .bai-card-actions{display:flex;flex-wrap:wrap;gap:4px;margin-top:auto}
      .bai-card-drawer{margin-top:10px;padding-top:10px;border-top:1px solid var(--bai-border,#e4e4e7);display:flex;flex-direction:column;gap:8px}
      .bai-field{display:flex;flex-direction:column;gap:3px}
      .bai-field-label{font-size:11px;font-weight:600;color:var(--bai-card-meta,#71717a);text-transform:uppercase;letter-spacing:.03em}
      .bai-field input[type="text"]{padding:7px 9px;border:1px solid var(--bai-border,#d4d4d8);border-radius:6px;font-size:13px;font-family:inherit;outline:none;background:#fff;color:var(--bai-card-fg,#18181b)}
      .bai-field input[type="text"]:focus{border-color:var(--bai-accent,#18181b)}
      .bai-field input[type="file"]{font-size:11px}
      .bai-field-hint{font-size:11px;color:#16a34a;font-weight:600}
      .bai-cust-textarea{width:100%;padding:8px 10px;border:1px solid var(--bai-border,#d4d4d8);border-radius:8px;font-size:13px;font-family:inherit;outline:none;resize:vertical;min-height:54px;background:var(--bai-input-bg,#f4f4f5);color:var(--bai-card-fg,#18181b);box-sizing:border-box;line-height:1.45;transition:border-color .15s}
      .bai-cust-textarea:focus{border-color:var(--bai-accent,#18181b);background:#fff}
      .bai-card-drawer-actions{display:flex;flex-wrap:wrap;gap:5px;margin-top:4px}

      .bai-btn{font-size:11px;font-weight:600;padding:6px 10px;border-radius:7px;border:none;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;line-height:1.3;transition:opacity .12s;font-family:inherit;white-space:nowrap}
      .bai-btn:hover:not(:disabled){opacity:.88}
      .bai-btn:disabled{opacity:.55;cursor:wait}
      .bai-btn--primary{background:var(--bai-accent,#18181b);color:var(--bai-accent-fg,#fff)}
      .bai-btn--cart{background:var(--bai-cart,#16a34a);color:#fff}
      .bai-btn--buynow{background:var(--bai-buynow,#2563eb);color:#fff}
      .bai-btn--ghost{background:var(--bai-ghost-bg,#f4f4f5);color:var(--bai-ghost-fg,#18181b)}
      .bai-btn--link{background:transparent;color:var(--bai-accent,#2563eb);text-decoration:underline;padding:6px 4px}

      .bai-preview-hint{font-size:12px;color:var(--bai-card-meta,#71717a);background:var(--bai-preview-bg,#fafafa);border-left:3px solid var(--bai-accent,#2563eb);padding:8px 12px;border-radius:6px;margin:10px 0}
      .bai-preview-orphan{font-size:12px;color:#a1a1aa;font-style:italic;margin:8px 0}

      .bai-toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#18181b;color:#fff;padding:10px 18px;border-radius:10px;font-size:13px;font-weight:600;box-shadow:0 8px 32px rgba(0,0,0,.2);z-index:99999;opacity:0;transition:opacity .2s,transform .2s;pointer-events:none}
      .bai-toast.show{opacity:1;transform:translateX(-50%) translateY(-4px)}
    `;
    document.head.appendChild(style);
  }

  // ──────────────────────────────────────────────────────────────────
  //  Public: render a single message → HTML string
  // ──────────────────────────────────────────────────────────────────
  async function renderMessage(content, role, opts) {
    injectSharedCss();
    const { apiBase = '', shopHost = '', productCache = {}, userImageUrl, compact = false, messageKey = 'm0' } = opts || {};

    if (role === 'user') {
      const text = String(content || '').replace('[Image attached]', '').trim();
      const img = userImageUrl ? `<img class="bai-msg-img" src="${escapeHtml(userImageUrl)}" alt="Uploaded" />` : '';
      return `<div class="bai-msg bai-msg--user"><div class="bai-msg-bubble">${text ? escapeHtml(text) : '<em style="opacity:.7">[Image attached]</em>'}${img}</div></div>`;
    }

    if (!content) {
      return `<div class="bai-msg bai-msg--assistant"><div class="bai-msg-bubble bai-msg-bubble--rich"><span style="color:#a1a1aa">…</span></div></div>`;
    }

    const blocks = extractBlocks(content);
    const bodyHtml = await renderBlocks(blocks, { apiBase, shopHost, productCache, compact, messageKey });
    return `<div class="bai-msg bai-msg--assistant"><div class="bai-msg-bubble bai-msg-bubble--rich">${bodyHtml}</div></div>`;
  }

  return {
    escapeHtml,
    markdownToHtml,
    extractBlocks,
    renderMessage,
    handleCardAction,
    fetchProduct,
    addToCart,
    injectSharedCss,
    toast,
    cardState,
    getCardState,
    setCardState,
  };
})();
